/**
 * Fair Selector Component (Issue #1173 Enhancement)
 *
 * Dropdown component for selecting a fair from user's vendor reservations.
 * Used in vendor financial tracking pages (expense, revenue, ROI tracker).
 *
 * Features:
 * - Loads all reservations for the current vendor
 * - Groups fairs with confirmed reservations
 * - Auto-selects fair from URL parameter
 * - Navigates to selected fair on change
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Loader2, AlertCircle } from 'lucide-react';
import { vendorApi } from '../services/api';
import { VendorReservation } from '../types';
import { extractApiError } from '../utils/errorUtils';

interface FairSelectorProps {
  vendorId: number;
  currentFairId: number | null;
  basePath: 'revenue' | 'expenses' | 'roi-tracker';
}

export const FairSelector: React.FC<FairSelectorProps> = ({
  vendorId,
  currentFairId,
  basePath,
}) => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState<VendorReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReservations = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await vendorApi.getVendorReservations(vendorId);
        // Filter to confirmed reservations only
        const confirmed = data.filter(r => r.status === 'confirmed');
        setReservations(confirmed);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        setError(apiError.message);
        console.error('Failed to load reservations:', apiError);
      } finally {
        setLoading(false);
      }
    };

    loadReservations();
  }, [vendorId]);

  const handleFairChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const fairId = parseInt(event.target.value, 10);
    if (fairId && !isNaN(fairId)) {
      navigate(`/vendor/fairs/${fairId}/${basePath}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading fairs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600">
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  if (reservations.length === 0) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Calendar className="w-4 h-4" />
        <span className="text-sm">No fair reservations yet</span>
      </div>
    );
  }

  // Get unique fairs (in case vendor has multiple slots at same fair)
  const uniqueFairs = Array.from(
    new Map(reservations.map(r => [r.fair_id, r])).values()
  );

  return (
    <div className="flex items-center gap-3">
      <label htmlFor="fair-selector" className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <Calendar className="w-4 h-4" />
        Select Fair:
      </label>
      <select
        id="fair-selector"
        value={currentFairId || ''}
        onChange={handleFairChange}
        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        data-testid="fair-selector-dropdown"
      >
        {!currentFairId && (
          <option value="" disabled>
            Choose a fair...
          </option>
        )}
        {uniqueFairs.map(reservation => (
          <option key={reservation.fair_id} value={reservation.fair_id}>
            {reservation.fair_name}
          </option>
        ))}
      </select>
    </div>
  );
};
